import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Navbar as BsNavbar, Nav, Container, NavDropdown, Button } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt, faUser, faUsers, faUniversity, faHandsHelping, faLayerGroup, faGift, faSun, faMoon } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useIsDashboard, useIsAuthPage } from '../../utils/routeUtils';
import api, { API_ORIGIN } from '../../services/api';
import AuthModal from '../auth/AuthModal';
import { Modal, Form, Alert } from 'react-bootstrap';
import './Navbar.css';

const Navbar = React.memo(() => {
  const [expanded, setExpanded] = useState(false);
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const isDashboard = useIsDashboard();
  const isAuthPage = useIsAuthPage();
  const navigate = useNavigate();
  const location = useLocation();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [courseCategories, setCourseCategories] = useState<{ name: string; count: number }[]>([]);
  type NavChild = { label: string; url: string; visible?: boolean };
  type NavItem = { type?: 'link' | 'dropdown'; label: string; url?: string; visible?: boolean; children?: NavChild[] };
  const [customLinks, setCustomLinks] = useState<NavItem[]>([]);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordStatus, setPasswordStatus] = useState({ type: '', message: '', loading: false });

  useEffect(() => {
    let active = true;
    async function loadCourses() {
      try {
        const cached = sessionStorage.getItem('course_categories');
        if (cached && active) {
          const parsed = JSON.parse(cached);
          setCourseCategories(parsed);
          // Still fetch in background to refresh cache
        }

        const [categoriesRes, coursesRes] = await Promise.all([
          api.categories.list(),
          api.courses.list('')
        ]);
        
        const categories = Array.isArray(categoriesRes) ? categoriesRes : [];
        const courses = coursesRes.items || [];
        
        const map = new Map<string, number>();
        
        // Initialize map with all categories from the official categories table
        categories.forEach((cat: any) => {
          map.set(cat.name, 0);
        });

        // Count courses in each category
        for (const c of courses) {
          if (!c) continue;
          // Use category_name from the joined query or fallback to legacy category field
          const catName = c.category_name || c.category;
          if (catName) {
            map.set(catName, (map.get(catName) || 0) + 1);
          }
        }
        
        const cats = Array.from(map.entries())
          .map(([name, count]) => ({ name, count }))
          .filter(cat => cat.name !== 'General') // Remove 'General' category
          .sort((a, b) => a.name.localeCompare(b.name));
        
        if (active) {
          setCourseCategories(cats);
          sessionStorage.setItem('course_categories', JSON.stringify(cats));
        }
      } catch (err) {
        console.error('Failed to load courses/categories in Navbar:', err);
        if (active) setCourseCategories([]);
      }
    }
    loadCourses();
    return () => {
      active = false;
    };
  }, []);

  // Load custom navigation links from settings (with localStorage fallback)
  useEffect(() => {
    let mounted = true;
    try {
      const cached = localStorage.getItem('nav_links');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && mounted) setCustomLinks(parsed);
      }
    } catch { }
    async function loadSettingsLinks() {
      try {
        const data = await api.settings.get();
        const links = (data?.nav_links || data?.navLinks || []) as Array<any>;
        const normalized: NavItem[] = Array.isArray(links)
          ? links
            .filter((l) => l && l.label)
            .map((l) => ({
              type: l.type === 'dropdown' ? 'dropdown' : 'link',
              label: String(l.label),
              url: l.url ? String(l.url) : undefined,
              visible: l.visible !== false,
              children: Array.isArray(l.children)
                ? l.children
                  .filter((c: any) => c && c.label && c.url)
                  .map((c: any) => ({ label: String(c.label), url: String(c.url), visible: c.visible !== false }))
                : undefined,
            }))
          : [];
        if (mounted) setCustomLinks(normalized);
        try { localStorage.setItem('nav_links', JSON.stringify(normalized)); } catch { }
      } catch (e) {
        if (mounted) {
          try {
            const cached = localStorage.getItem('nav_links');
            if (cached) setCustomLinks(JSON.parse(cached));
          } catch { }
        }
      }
    }
    loadSettingsLinks();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const authAction = params.get('auth');
    if (authAction === 'login') {
      setAuthMode('login');
      setShowAuth(true);
      // Clean up URL
      navigate(location.pathname, { replace: true });
    }

    const state = location.state as any;
    if (state?.openLogin) {
      setAuthMode(state.authMode === 'signup' ? 'signup' : 'login');
      setShowAuth(true);
      // Clean up the state so it doesn't keep opening on every re-render
      navigate(location.pathname, { replace: true, state: { ...state, openLogin: undefined, authMode: undefined } });
    }
  }, [location, navigate]);

  const isAuthed = useMemo(() => {
    try {
      return !!localStorage.getItem('token');
    } catch {
      return false;
    }
  }, []);

  const closeNavbar = useCallback(() => setExpanded(false), []);

  const openAuth = useCallback((mode: 'login' | 'signup') => {
    closeNavbar();
    setAuthMode(mode);
    setShowAuth(true);
  }, [closeNavbar]);

  const brandTo = useMemo(() => {
    if (!user) return '/';
    return user.role === 'admin' ? '/admin/dashboard' : '/my-dashboard';
  }, [user]);

  const handleLogout = useCallback(async () => {
    try {
      setExpanded(false);
      await logout();
      navigate('/', { replace: true });
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/');
      window.location.reload();
    }
  }, [logout, navigate]);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordStatus({ type: 'danger', message: 'New passwords do not match', loading: false });
      return;
    }
    setPasswordStatus({ type: '', message: '', loading: true });
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ORIGIN}/api/auth/change-password`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          oldPassword: passwordForm.oldPassword,
          newPassword: passwordForm.newPassword
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to change password');
      
      setPasswordStatus({ type: 'success', message: 'Password changed successfully!', loading: false });
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordStatus({ type: '', message: '', loading: false });
      }, 2000);
    } catch (err: any) {
      setPasswordStatus({ type: 'danger', message: err.message, loading: false });
    }
  };

  if (isDashboard || isAuthPage) {
    return null;
  }

  return (
    <div className="modern-navbar-wrapper">
      <AuthModal
        show={showAuth}
        onHide={() => setShowAuth(false)}
        defaultMode={authMode}
      />
      <BsNavbar
        expand="lg"
        className="modern-navbar shadow-sm"
        expanded={expanded}
        onToggle={(val) => setExpanded(val)}
      >
        <Container fluid className="px-4">
          <div className="navbar-header-mobile d-flex align-items-center w-100 justify-content-between">
            
            {/* 1. LEFT SECTION: Logo */}
            <div className="navbar-brand-section d-flex align-items-center">
              <LinkContainer to={brandTo} onClick={closeNavbar}>
                <div className="brand-container d-flex align-items-center cursor-pointer">
                  <div className="brand-icon-wrapper me-lg-2">
                    <img
                      src="/logo.png"
                      width="36"
                      height="36"
                      alt="DataPylot Logo"
                    />
                  </div>
                  <div className="brand-text d-none d-lg-block">
                    <BsNavbar.Brand className="brand-name mb-0">DataPylot</BsNavbar.Brand>
                  </div>
                </div>
              </LinkContainer>
            </div>

            {/* Mobile Toggle (only on mobile) */}
            <div className="d-lg-none">
              <BsNavbar.Toggle
                aria-controls="basic-navbar-nav"
                onClick={() => setExpanded(!expanded)}
                className="border-0 p-0 modern-toggler"
              >
                <span className="navbar-toggler-icon"></span>
              </BsNavbar.Toggle>
            </div>

            {/* 2. MIDDLE SECTION: Navigation Links */}
            <BsNavbar.Collapse id="basic-navbar-nav" className="flex-grow-0">
              <Nav className="align-items-lg-center flex-column flex-lg-row gap-lg-4 nav-center-desktop">
                <NavLink 
                  to="/" 
                  end 
                  className={({ isActive }) => `nav-link nav-link-modern px-2 ${isActive ? 'active' : ''}`}
                  onClick={closeNavbar}
                >
                  Home
                </NavLink>

                <NavLink 
                  to="/career-paths" 
                  className={({ isActive }) => `nav-link nav-link-modern px-2 ${isActive ? 'active' : ''}`}
                  onClick={closeNavbar}
                >
                  Career Paths
                </NavLink>

                <NavDropdown title="Courses" id="courses-dropdown" className={`nav-dropdown-modern mega-dropdown px-2 ${location.pathname.startsWith('/courses') ? 'active' : ''}`}>
                  <div className="mega-dropdown-menu p-4" style={{ minWidth: '550px' }}>
                    <div className="row g-4">
                      <div className="col-12">
                        <h6 className="text-uppercase text-muted small mb-3 fw-bold" style={{ letterSpacing: '1px' }}>Explore Tracks</h6>
                        <div className="mega-menu-columns">
                          <LinkContainer to="/courses" onClick={closeNavbar}>
                            <NavDropdown.Item className="dropdown-item-modern fw-semibold category-grid-item">All Categories</NavDropdown.Item>
                          </LinkContainer>
                          {courseCategories.map((cat) => (
                            <LinkContainer
                              key={cat.name}
                              to={{ pathname: '/courses', search: `?category=${encodeURIComponent(cat.name)}` }}
                              onClick={closeNavbar}
                            >
                              <NavDropdown.Item className="dropdown-item-modern d-flex justify-content-between align-items-center category-grid-item">
                                <span>{cat.name}</span>
                                {cat.count > 0 && (
                                  <span className="badge-count ms-2">{cat.count}</span>
                                )}
                              </NavDropdown.Item>
                            </LinkContainer>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </NavDropdown>

                <Nav.Link href="/#about" className="nav-link-modern px-2" onClick={closeNavbar}>About Us</Nav.Link>
                <Nav.Link href="/#partners" className="nav-link-modern px-2" onClick={closeNavbar}>Partners</Nav.Link>
                <Nav.Link href="/#contact" className="nav-link-modern px-2" onClick={closeNavbar}>Contact Us</Nav.Link>

                {/* Custom links */}
                {customLinks.filter(l => l.visible !== false).map((item) => {
                  if (item.type === 'dropdown' && item.children && item.children.length) {
                    return (
                      <NavDropdown title={item.label} id={`custom-dd-${item.label}`} className="nav-dropdown-modern px-2" key={`dd-${item.label}`}>
                        {item.children.filter(c => c.visible !== false).map((child) => {
                          const isExternal = child.url ? /^https?:\/\//i.test(child.url) : false;
                          return isExternal ? (
                            <NavDropdown.Item key={child.label} href={child.url} target="_blank" rel="noopener noreferrer" onClick={closeNavbar} className="dropdown-item-modern">{child.label}</NavDropdown.Item>
                          ) : (
                            <LinkContainer key={child.label} to={child.url || '#'} onClick={closeNavbar}>
                              <NavDropdown.Item className="dropdown-item-modern">{child.label}</NavDropdown.Item>
                            </LinkContainer>
                          );
                        })}
                      </NavDropdown>
                    );
                  }
                  return (
                    <LinkContainer key={item.label} to={item.url || '#'} onClick={closeNavbar}>
                      <Nav.Link className="nav-link-modern px-2">{item.label}</Nav.Link>
                    </LinkContainer>
                  );
                })}

                {/* MOBILE ONLY AUTH SECTION */}
                <div className="d-lg-none mt-4 pt-4 border-top w-100">
                  {user ? (
                    <div className="d-flex flex-column gap-3">
                      <div className="d-flex align-items-center gap-3 px-3 mb-3">
                        <div className="avatar-small">{user?.username?.charAt(0)}</div>
                        <span className="fw-bold">{user?.username}</span>
                      </div>
                      <LinkContainer to={user.role === 'admin' ? '/admin/dashboard' : '/my-dashboard'} onClick={closeNavbar}>
                        <Button variant="outline-primary" className="w-100 text-start">Dashboard</Button>
                      </LinkContainer>
                      <Button variant="danger" className="w-100 text-start" onClick={handleLogout}>Logout</Button>
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-3 px-2">
                      {/* Login and Sign Up buttons hidden */}
                      {/* <Button className="btn-modern btn-signin w-100" onClick={() => openAuth('login')}>Login</Button> */}
                      {/* <Button className="btn-modern btn-cta-pill w-100" onClick={() => openAuth('signup')}>Sign Up</Button> */}
                    </div>
                  )}
                </div>
              </Nav>
            </BsNavbar.Collapse>

            {/* 3. RIGHT SECTION: Auth Buttons (Desktop only) */}
            <div className="d-none d-lg-flex align-items-center justify-content-end gap-3 navbar-auth-desktop">
              {user ? (
                 <NavDropdown 
                    title={<span className="fw-bold text-dark text-uppercase small">{user?.username?.split(' ')[0]}</span>}
                    id="user-profile-dropdown" 
                    align="end" 
                    className="profile-dropdown-modern"
                 >
                   <LinkContainer to={user.role === 'admin' ? '/admin/dashboard' : '/my-dashboard'} onClick={closeNavbar}>
                     <NavDropdown.Item className="dropdown-item-modern">Dashboard</NavDropdown.Item>
                   </LinkContainer>
                   <NavDropdown.Item className="dropdown-item-modern text-danger" onClick={handleLogout}>Logout</NavDropdown.Item>
                 </NavDropdown>
              ) : (
                <>
                  {/* Login and Sign Up buttons hidden */}
                  {/* <Button className="btn-modern btn-signin" onClick={() => openAuth('login')}>Login</Button> */}
                  {/* <Button className="btn-modern btn-cta-pill" onClick={() => openAuth('signup')}>Sign Up</Button> */}
                </>
              )}
            </div>

          </div>
        </Container>
      </BsNavbar>
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered>

        <Modal.Header closeButton>
          <Modal.Title className="h5 fw-bold">Change Password</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {passwordStatus.message && (
            <Alert variant={passwordStatus.type as any} className="small py-2 mb-3">
              {passwordStatus.message}
            </Alert>
          )}
          <Form onSubmit={handleChangePassword}>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold text-muted">Current Password</Form.Label>
              <Form.Control
                type="password"
                required
                placeholder="Enter current password"
                value={passwordForm.oldPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                className="bg-light border-0"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold text-muted">New Password</Form.Label>
              <Form.Control
                type="password"
                required
                placeholder="Enter new password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                className="bg-light border-0"
              />
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label className="small fw-semibold text-muted">Confirm New Password</Form.Label>
              <Form.Control
                type="password"
                required
                placeholder="Confirm new password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                className="bg-light border-0"
              />
            </Form.Group>
            <Button
              type="submit"
              className="w-100 fw-bold py-2 btn-modern btn-modern-primary"
              disabled={passwordStatus.loading}
            >
              {passwordStatus.loading ? 'Updating...' : 'Update Password'}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
});

export default Navbar;
